// apps/web/src/components/AutoCaptureIDWebcam.tsx

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Zap, ZapOff, Camera, CreditCard } from 'lucide-react';

interface AutoCaptureIDWebcamProps {
  onCapture: (file: File) => void;
  capturedImage: string | null;
  onRecapture: () => void;
}

export const AutoCaptureIDWebcam = ({ onCapture, capturedImage, onRecapture }: AutoCaptureIDWebcamProps) => {
  const webcamRef = useRef<Webcam>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [feedback, setFeedback] = useState('Position your ID within the frame');
  const [autoCaptureCooldown, setAutoCaptureCooldown] = useState(false);
  const lastCaptureTimeRef = useRef(0);
  const frameCountRef = useRef(0);

  // Check for flashlight (torch) support on the user's device
  useEffect(() => {
    // SAFEGUARD: Check if mediaDevices API is supported (e.g., hidden on insecure HTTP)
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const videoDevice = devices.find(d => d.kind === 'videoinput');
        // Note: getCapabilities() might also be missing in some browsers
        if ((videoDevice as any)?.getCapabilities && (videoDevice as any).getCapabilities()?.torch) {
          setTorchSupported(true);
        }
      }).catch(err => {
        console.warn("Failed to enumerate devices:", err);
      });
    } else {
      console.warn("Camera API not fully supported (likely insecure origin). Flashlight disabled.");
    }
  }, []);

  // Update feedback based on camera readiness
  useEffect(() => {
    if (isCameraReady) {
      setFeedback('Align your ID card within the guide');
    }
  }, [isCameraReady]);

  // --- SIMPLE, RELIABLE MANUAL CAPTURE ---
  const handleCapture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      fetch(imageSrc).then(res => res.blob()).then(blob => {
        const file = new File([blob], "national-id.jpg", { type: "image/jpeg" });
        onCapture(file);
        setFeedback('ID captured successfully!');
      });
    } else {
      toast.error("Could not capture image. Please ensure camera permissions are enabled.");
    }
  }, [webcamRef, onCapture]);

  const toggleTorch = useCallback(async () => {
    const track = (webcamRef.current?.video?.srcObject as MediaStream)?.getVideoTracks()[0];
    if ((track?.getCapabilities() as any).torch) {
      try {
        await track.applyConstraints({ advanced: [{ torch: !isTorchOn } as any] });
        setIsTorchOn(!isTorchOn);
      } catch (err) {
        toast.error("Could not toggle flashlight.");
      }
    }
  }, [isTorchOn]);

  return (
    <div className="space-y-4">
      <div className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden border flex items-center justify-center">
        {capturedImage ? (
          <img src={capturedImage} alt="ID Capture" className="w-full h-full object-contain" />
        ) : (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              mirrored={false}
              className="w-full h-full object-cover"
              videoConstraints={{ facingMode: "environment", width: 1280, height: 720 }}
              onUserMedia={() => setIsCameraReady(true)}
              onUserMediaError={() => toast.error("Camera access was denied or is unavailable.")}
            />

            {/* ID Card Alignment Guide */}
            {isCameraReady && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-[80%] aspect-[1.6/1] border-4 border-primary rounded-lg shadow-lg">
                  {/* Corner markers */}
                  <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg"></div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg"></div>
                  <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg"></div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg"></div>

                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <CreditCard className="w-12 h-12 text-primary/30" />
                  </div>
                </div>
              </div>
            )}

            {!isCameraReady && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>}
          </>
        )}

        {/* Feedback message */}
        {!capturedImage && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full backdrop-blur-sm z-10">
            {feedback}
          </div>
        )}
      </div>
      <div className="flex justify-center gap-2">
        {capturedImage ? (
          <Button type="button" onClick={onRecapture} className="w-full" variant="outline"><RefreshCw className="mr-2 h-4 w-4" />Recapture ID</Button>
        ) : (
          <div className="flex w-full items-center gap-2">
            <Button type="button" onClick={handleCapture} className="flex-grow" variant="premium" disabled={!isCameraReady}>
              <Camera className="mr-2 h-4 w-4" /> Capture ID
            </Button>
            {torchSupported && (
              <Button type="button" onClick={toggleTorch} variant="secondary" size="icon" aria-label="Toggle Flashlight">{isTorchOn ? <ZapOff /> : <Zap />}</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoCaptureIDWebcam;